import { apiClient } from './api.client';

/**
 * SP dynamic PDF templates (SP_DOCUMENT_ONBOARDING_PLAN.md) — admin API under
 * /api/admin/form-templates plus the investor-facing /api/investor/form-templates.
 * Types mirror fs-platform-api module/formtemplate/dto.
 */

export type TemplateStatus =
  | 'UPLOADED' | 'DISCOVERED' | 'FLAT' | 'MAPPING_IN_PROGRESS' | 'VALIDATED' | 'ACTIVE' | 'RETIRED';
export type TemplateType = 'BOOKLET' | 'CKYC' | 'FATCA' | 'OTHER';
export type MatchStatus = 'AUTO_MATCHED' | 'FLAGGED' | 'CONFIRMED' | 'NOT_APPLICABLE';
export type MapType = 'DIRECT' | 'CONSTANT' | 'DATE_SPLIT' | 'CHAR_SPLIT' | 'ENUM_SET' | 'PRESENCE' | 'LOOKUP';

export interface PatternNotApplicableResult {
  applied: boolean;
  matched: number;
  flaggedBefore: number;
  fields: string[];
  template: TemplateSummary;
}

export interface TemplateSummary {
  id: number;
  spRef: string;
  spName: string | null;
  spType: string | null;
  name: string;
  templateType: TemplateType;
  version: number;
  status: TemplateStatus;
  formType: string | null;
  pageCount: number | null;
  fieldCount: number | null;
  recognisedLibrary: string | null;
  assembled: boolean;
  /** Assembled booklets this template is a component of; such a template cannot be activated on its own. */
  usedInBooklets: string[];
  originalFilename: string | null;
  autoMatched: number;
  flagged: number;
  confirmed: number;
  notApplicable: number;
  lastValidationSummary: string | null;
  createdBy: string | null;
  createdAt: unknown;
  modifiedAt: unknown;
}

export interface FieldRow {
  id: number;
  pdfField: string;
  fieldType: string | null;
  page: number | null;
  nearbyText: string | null;
  suggestedName: string | null;
  options: string | null;
  matchStatus: MatchStatus;
  mapType: MapType | null;
  investorField: string | null;
  constantValue: string | null;
  splitConfig: string | null;
  matchSource: string | null;
  /** What the library seed maps this field to, when that differs from the row's current decision. */
  seedSuggestion?: string | null;
}

export interface TemplateDetail {
  template: TemplateSummary;
  fields: FieldRow[];
  /** Rows whose decision differs from the library seed (a seed fix hiding behind an older N/A). */
  seedDiffers?: number;
}

export interface LibraryMatch {
  code: string;
  displayName: string;
  inventorySize: number;
  overlap: number;
  recognised: boolean;
  seededFields: number;
}

export interface UploadResult {
  template: TemplateSummary;
  libraryCheck: LibraryMatch[];
  nameMatched: number;
  message: string;
}

export interface InvestorField {
  name: string;
  label: string;
  group: string;
  date: boolean;
}

export interface ServiceProviderOption {
  /** Provider GUID — what investor_journey.sp_ref carries. */
  spRef: string;
  spName: string;
  code: string | null;
  spType: string;
  /** Journeys under this provider in Dataverse (platform count when CRM is unreachable). */
  journeyCount: number;
  /** Journeys already synced into this environment. */
  platformJourneyCount: number;
  journeyCountSource: 'DATAVERSE' | 'PLATFORM';
}

export interface LibraryForm {
  code: string;
  displayName: string;
  fieldCount: number;
}

export interface ValidationResult {
  valid: boolean;
  inventorySize: number;
  producedFields: number;
  flaggedFields: number;
  errors: string[];
  warnings: string[];
  unmappedPdfFields: string[];
  summary: string;
}

export interface FieldUpdate {
  fieldId: number;
  action: 'MAP' | 'CONFIRM' | 'NOT_APPLICABLE' | 'RESET';
  mapType?: MapType;
  investorField?: string;
  constantValue?: string;
  splitConfig?: string;
}

export interface AssembleItem {
  sourceType: 'TEMPLATE' | 'LIBRARY';
  templateId?: number;
  libraryCode?: string;
}

export interface AssembleRequest {
  spRef: string;
  spName?: string;
  spType?: string;
  name?: string;
  items: AssembleItem[];
}

export interface Component {
  id: number;
  sequence: number;
  sourceType: 'TEMPLATE' | 'LIBRARY';
  sourceRef: string;
  displayName: string | null;
  fieldPrefix: string;
  pageCount: number | null;
}

class FormTemplateService {
  private baseUrl = '/api/admin/form-templates';

  async list(spRef?: string): Promise<TemplateSummary[]> {
    const response = await apiClient.get<TemplateSummary[]>(this.baseUrl, {
      params: spRef ? { spRef } : undefined,
    });
    return response.data;
  }

  async upload(params: {
    spRef: string; spName?: string; spType?: string; name?: string; templateType: TemplateType; file: File;
  }): Promise<UploadResult> {
    const form = new FormData();
    form.append('spRef', params.spRef);
    if (params.spName) form.append('spName', params.spName);
    if (params.spType) form.append('spType', params.spType);
    if (params.name) form.append('name', params.name);
    form.append('templateType', params.templateType);
    form.append('file', params.file);
    const response = await apiClient.post<UploadResult>(this.baseUrl, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async detail(id: number, status?: MatchStatus): Promise<TemplateDetail> {
    const response = await apiClient.get<TemplateDetail>(`${this.baseUrl}/${id}`, {
      params: status ? { status } : undefined,
    });
    return response.data;
  }

  async delete(id: number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async templatePdf(id: number): Promise<Blob> {
    const response = await apiClient.get<Blob>(`${this.baseUrl}/${id}/pdf`, { responseType: 'blob' });
    return response.data;
  }

  async investorFields(): Promise<InvestorField[]> {
    const response = await apiClient.get<InvestorField[]>(`${this.baseUrl}/investor-fields`);
    return response.data;
  }

  /** Providers from the Dataverse-synced masters; the SP picker on the admin screens. */
  async serviceProviders(): Promise<ServiceProviderOption[]> {
    const response = await apiClient.get<ServiceProviderOption[]>(`${this.baseUrl}/service-providers`);
    return response.data;
  }

  async library(): Promise<LibraryForm[]> {
    const response = await apiClient.get<LibraryForm[]>(`${this.baseUrl}/library`);
    return response.data;
  }

  // ── mapping workflow ──

  async updateFields(id: number, updates: FieldUpdate[]): Promise<TemplateSummary> {
    const response = await apiClient.put<TemplateSummary>(`${this.baseUrl}/${id}/fields`, updates);
    return response.data;
  }

  async confirmAuto(id: number): Promise<TemplateSummary> {
    const response = await apiClient.post<TemplateSummary>(`${this.baseUrl}/${id}/fields/confirm-auto`);
    return response.data;
  }

  async markNotApplicable(id: number): Promise<TemplateSummary> {
    const response = await apiClient.post<TemplateSummary>(`${this.baseUrl}/${id}/fields/not-applicable`);
    return response.data;
  }

  async validate(id: number): Promise<ValidationResult> {
    const response = await apiClient.post<ValidationResult>(`${this.baseUrl}/${id}/validate`);
    return response.data;
  }

  async preview(id: number, uniqueCode?: string): Promise<Blob> {
    const response = await apiClient.post<Blob>(`${this.baseUrl}/${id}/preview`, null, {
      params: uniqueCode ? { uniqueCode } : undefined,
      responseType: 'blob',
    });
    return response.data;
  }

  async activate(id: number): Promise<TemplateSummary> {
    const response = await apiClient.post<TemplateSummary>(`${this.baseUrl}/${id}/activate`);
    return response.data;
  }

  async retire(id: number): Promise<TemplateSummary> {
    const response = await apiClient.post<TemplateSummary>(`${this.baseUrl}/${id}/retire`);
    return response.data;
  }

  async mapping(id: number): Promise<unknown> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/mapping`);
    return response.data;
  }

  // ── booklet composer ──

  async assemble(request: AssembleRequest): Promise<UploadResult> {
    const response = await apiClient.post<UploadResult>(`${this.baseUrl}/assemble`, request);
    return response.data;
  }

  /** Preview (dryRun) or apply "not applicable" to still-flagged rows whose PDF field name matches a glob. */
  async notApplicableByPattern(id: number, patterns: string[], dryRun: boolean): Promise<PatternNotApplicableResult> {
    const response = await apiClient.post<PatternNotApplicableResult>(
      `${this.baseUrl}/${id}/fields/not-applicable-by-pattern`, { patterns, dryRun });
    return response.data;
  }

  /** Globs the server marks not applicable automatically at upload — the default for the pattern box. */
  async autoNaPatterns(): Promise<string[]> {
    const response = await apiClient.get<string[]>(`${this.baseUrl}/auto-na-patterns`);
    return response.data;
  }

  /** Re-apply component mappings / N/A decisions / remembered library decisions to still-flagged rows. */
  async reseed(id: number): Promise<TemplateSummary> {
    const response = await apiClient.post<TemplateSummary>(`${this.baseUrl}/${id}/reseed`);
    return response.data;
  }

  async components(id: number): Promise<Component[]> {
    const response = await apiClient.get<Component[]>(`${this.baseUrl}/${id}/components`);
    return response.data;
  }
}

class InvestorFormTemplateService {
  private baseUrl = '/api/investor/form-templates';

  /** Forms the investor's service provider configured; empty when none. */
  async list(journeyId?: string): Promise<TemplateSummary[]> {
    const response = await apiClient.get<TemplateSummary[]>(this.baseUrl, {
      params: journeyId ? { journeyId } : undefined,
    });
    return response.data;
  }

  async download(id: number, journeyId?: string): Promise<Blob> {
    const response = await apiClient.get<Blob>(`${this.baseUrl}/${id}/download`, {
      params: journeyId ? { journeyId } : undefined,
      responseType: 'blob',
    });
    return response.data;
  }
}

export const formTemplateService = new FormTemplateService();
export const investorFormTemplateService = new InvestorFormTemplateService();

/** Open a PDF blob in a new tab, falling back to a download when pop-ups are blocked. */
export function openPdfBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const opened = window.open(url, '_blank');
  if (!opened) {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}
